import { observer } from 'mobx-react';

import * as storage from '../../utils/storage';
import Loading from '../Loading/Loading';
import Modal from 'react-native-modal';
import { COLORS } from '../../config/designSystem/Colors';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { Linking } from 'react-native';
import { Redirect, router, useGlobalSearchParams, useUnstableGlobalHref } from 'expo-router';
import { STORE_ELLI_TOKEN } from '../../state/mutations/User';
import { addTrigger } from '../../utils/onesignal/addTrigger';
import { client } from '../../config/apollo/client';
import { concatPath } from '../../utils/links/concatPath';
import { elliRedirectStringForStore, elliRedirectUrlParam, elliTokenStringForStore, elliTokenUrlParam, redirectStringForStore } from '../../constants/storageKeys';
import { logAmplitude } from '../../utils/analytics/analytics';
import { loginKey, onlyPublicKeys } from '../../constants/routeKeys';
import { publicKey, settingKey, settingStackKey, tabsKey } from '../../constants/routeKeys';
import { removeIAMClickHandler, setIAMClickHandler } from '../../utils/onesignal/setIAMClickHandler';
import { useLogout } from '../../hooks/authentification/useLogout';
import { useModalEndOfTrial } from '../../hooks/modal/useModalEndOfTrial';
import { useOnboardingRedirection } from '../../hooks/navigation/useOnboardingRedirection';
import { useOneSignal } from './OneSignalContext';
import { useSilentLogin } from '../../hooks/authentification/useSilentLogin';
import { useStores } from '../../hooks/useStores';

export const AuthenticatedApp : FC<PropsWithChildren> = observer(({children}) => {
  const rootStore = useStores();
  const labels = rootStore?.language?.translate?.MOBILE?.ROOT;
  const hasUser = !!rootStore.user.user;
  const isAnon = !!rootStore.user.anonymous;
  const isRegisteredUser = hasUser && !isAnon;
  const [modalVisible, setModalVisible] = useState(false);
  const [initLogin, setInitLogin] = useState(false);
  const [wasInPrivate, setWasInPrivate] = useState(false);
  const [ftiHandled, setFtiHandled] = useState(false);

  const params = useGlobalSearchParams();
  const globalHref = useUnstableGlobalHref();

  const logoutCb =() => {
    setInitLogin(true);
    setModalVisible(false);
  };

  const logoutCbVolontary =() => {
    logoutCb();
  };

  const logout = useLogout(logoutCb);
  const logoutVolontary = useLogout(logoutCbVolontary);
  const {manageLogin} = useSilentLogin({setInitLogin, logout, setModalVisible});
  const {modal} = useModalEndOfTrial({ logout: logoutVolontary, manageLogin });

  const {isInitialized} = useOneSignal();
  const {prefetchRedirection, redirect} = useOnboardingRedirection();





  useEffect(() => {
    // this use effect save the url the user wants to reach
    //
    // `hasUser` is false for the whole boot window, not just on the public screens, so
    // this also fires while the router is still settling and globalHref is a transient
    // value like '/' or '/_sitemap'. Storing one of those meant the NEXT launch replayed
    // it through router.navigate() in useManageRedirect, which lands on +not-found --
    // the "Oops, this page does not exist" seen on first load. A stored redirect is only
    // useful if it names a real destination, so ignore the placeholders.
    const href = globalHref ?? '';
    const isRealDestination = href.length > 1 && href !== '/' && !href.startsWith('/_');
    if (isRealDestination && !hasUser &&
      (!onlyPublicKeys.some(key =>href.substring(1) === key))
    && !(href.includes(settingKey) && wasInPrivate)) { // substring(1) to remove the /
      storage.saveString(redirectStringForStore,href);
    }
  }, [hasUser,globalHref]);






  useEffect(() => {
    if (isRegisteredUser) {
      setWasInPrivate(true);
      logAmplitude('Private App Launched');
      prefetchRedirection();
      removeRedirectStringFromStore();
    }
  }, [isRegisteredUser]);

  // Handle Elli token for already authenticated users
  useEffect(() => {
    const handleElliToken = async () => {
      if (!isRegisteredUser || ftiHandled) return;

      // Check URL params first (new elli-token param)
      let elliToken = params[elliTokenUrlParam] as string | undefined;
      let elliRedirectUrl = params[elliRedirectUrlParam] as string | undefined;

      // If not in URL, check storage (might have been saved before auth)
      if (!elliToken) {
        elliToken = await storage.loadString(elliTokenStringForStore) || undefined;
      }
      if (!elliRedirectUrl) {
        elliRedirectUrl = await storage.loadString(elliRedirectStringForStore) || undefined;
      }

      if (elliToken && elliRedirectUrl) {
        setFtiHandled(true);
        try {
          const { data } = await client.mutate({
            mutation: STORE_ELLI_TOKEN,
            variables: { elliToken },
          });
          if (data?.storeElliToken) {
            // Clear the token and redirect URL from storage
            storage.remove(elliTokenStringForStore);
            storage.remove(elliRedirectStringForStore);
            Linking.openURL(elliRedirectUrl);
          }
        } catch (error) {
          console.error('Failed to store Elli token:', error);
        }
      }
    };

    handleElliToken();
  }, [isRegisteredUser, params, ftiHandled]);

  useEffect(()=> {
    if(isInitialized && isRegisteredUser) {
      addTrigger('private_part_launched', String(!!rootStore?.onboarding?.onboardingShow));
    }
  }, [isInitialized, isRegisteredUser]);




  const removeRedirectStringFromStore = async () => {

    const redirectBasedOnStore = await storage.loadString(redirectStringForStore);
    if(redirectBasedOnStore) {
      storage.remove(redirectStringForStore);
    }
  };





  useEffect(() => {
    if(isInitialized) {
      const listener = setIAMClickHandler(value => {

        if(value === 'to_evolutime') {
          router.navigate({
            pathname: concatPath([settingStackKey, settingKey]) as string,
            params: {
              notif: encodeURIComponent(true)
            }});
        }
        if(value === 'to_onb_survey') {
          redirect(true);
        }
      });

      return () => {
        removeIAMClickHandler(listener);
      };
    }
  }, [isInitialized]);

  // fetch wpo appointments when app starts
  useEffect(() => {
    if (rootStore?.user?.company?.hasAccessToWpoAssistance) {
      const getWpoAppointments = async() => {
        await rootStore?.user?.wpoAppointments?.fetch();
      };
      void getWpoAppointments().catch(console.error);
    }
  }, [rootStore?.user?.company?.hasAccessToWpoAssistance, rootStore?.user?.wpoAppointments]);


  if(!isRegisteredUser) {
    // Absolute. concatPath(-1) now keeps its leading slash (see utils/links/concatPath),
    // so no manual prefix is needed here any more. Getting this wrong has bitten twice:
    // order 1 sent logout to /(private)/(public)/(Tabs)/login and 404'd, and the
    // slash-less "absolute" form crashed StackRouter.getRehydratedState on
    // `state.routes.filter(...)` -- a white screen right after login.
    return <Redirect href={concatPath([publicKey, tabsKey, loginKey], -1) as string} />;
  }


  if (!initLogin) {
    return (
      <Loading withLabel={true} label={labels?.LOADING ? labels?.LOADING : 'Loading data..'} color={COLORS.brand[500]}
      />
    );
  }

  return(<>
    <Modal
      isVisible={modalVisible}
    >
      {modal()}
    </Modal>
    {children}
  </>);


}
);



